import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScoreSimulator from "@/components/ScoreSimulator";
import api from "@/lib/api";
import useMotionPreferences from "@/hooks/useMotionPreferences";
import {
    SplitText,
    GradientText,
    BlurText,
    CountUp,
    Aurora,
    ScrollReveal,
    SpotlightCard,
    TiltCard,
    Magnet,
    Dock,
    ClickSpark,
    TypewriterText,
} from "@/components/animations";
import {
    ArrowRight,
    GraduationCap,
    CreditCard,
    Code2,
    Share2,
    Layers,
    ShoppingBag,
    LinkIcon,
    CheckCircle2,
    Brain,
    BarChart3,
    Search,
    ChevronRight,
    Sparkles,
    Globe,
    Trophy,
    ShieldCheck,
    Users,
    Database,
    Landmark,
} from "lucide-react";
import { EASING, DURATION } from "@/lib/motionSystem";
import { tierMeta } from "@/lib/tiers";

const trustStats = [
    { value: 400, suffix: "M+", label: "Credit-invisible Indians", testId: "stat-0" },
    { value: 1500000, suffix: "", label: "Students in network", testId: "stat-1" },
    { value: 900, suffix: "+", label: "Campuses covered", testId: "stat-2" },
    { value: 20, suffix: " Years", label: "Behavioral data depth", testId: "stat-3" },
];

const howItWorks = [
    {
        step: "01",
        icon: LinkIcon,
        title: "Connect",
        desc: "AI securely links your verified academic, financial, and digital signals in real-time.",
        color: "text-blue-primary",
        bg: "bg-blue-primary/10",
    },
    {
        step: "02",
        icon: CheckCircle2,
        title: "Verify",
        desc: "GenZGPT Agent validates records and confidence before generating your score.",
        color: "text-cyan-signal",
        bg: "bg-cyan-signal/10",
    },
    {
        step: "03",
        icon: Brain,
        title: "AI Analysis",
        desc: "Advanced AI blends multiple signals into one intelligent, explainable credit score.",
        color: "text-purple-soft",
        bg: "bg-purple-soft/10",
    },
    {
        step: "04",
        icon: BarChart3,
        title: "Instant Decisions",
        desc: "AI-powered insights unlock immediate credit opportunities with transparent evidence.",
        color: "text-green-pioneer",
        bg: "bg-green-pioneer/10",
    },
];

const signals = [
    {
        name: "Academic Intelligence",
        weight: 25,
        icon: GraduationCap,
        color: "#6366f1",
        detail: "CGPA, institution rigor, attendance, and trajectory.",
    },
    {
        name: "Fee Payment History",
        weight: 20,
        icon: CreditCard,
        color: "#06b6d4",
        detail: "Pattern quality, timing discipline, and payer consistency.",
    },
    {
        name: "Developer / Professional",
        weight: 20,
        icon: Code2,
        color: "#a855f7",
        detail: "GitHub depth, portfolio credibility, and profile maturity.",
    },
    {
        name: "Social / Creator",
        weight: 15,
        icon: Users,
        color: "#eab308",
        detail: "Audience consistency and behavior trust markers.",
    },
    {
        name: "Ecom Consumption",
        weight: 15,
        icon: ShoppingBag,
        color: "#ec4899",
        detail: "Online spend patterns, transaction consistency, and financial behavior signals.",
    },
    {
        name: "Coverage & Consistency",
        weight: 5,
        icon: Layers,
        color: "#22c55e",
        detail: "Cross-source agreement and freshness confidence.",
    },
];

const demoProfiles = [
    { key: "priya", label: "Priya", icon: Trophy },
    { key: "rahul", label: "Rahul", icon: Sparkles },
    { key: "arjun", label: "Arjun", icon: ShieldCheck },
];


function getDemoFallback(name) {
    const defaults = {
        priya: { name: "Priya Sharma", score: 804, tier: "Pioneer", confidence: 0.92, institution: "VJTI Mumbai" },
        rahul: { name: "Rahul Verma", score: 692, tier: "Builder", confidence: 0.83, institution: "VIT Vellore" },
        arjun: { name: "Arjun Menon", score: 578, tier: "Starter", confidence: 0.78, institution: "SRM Chennai" },
    };
    return defaults[name];
}

function FloatingCreditOrb() {
    const { allowDecorativeEffects } = useMotionPreferences();
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [shimmer, setShimmer] = useState(0);

    const onMouseMove = useCallback((event) => {
        if (!allowDecorativeEffects) return;
        const cx = window.innerWidth * 0.5;
        const cy = window.innerHeight * 0.33;
        const nx = (event.clientX - cx) / cx;
        const ny = (event.clientY - cy) / cy;
        setOffset({ x: nx * 10, y: ny * 8 });
    }, [allowDecorativeEffects]);

    useEffect(() => {
        if (!allowDecorativeEffects) return undefined;
        window.addEventListener("mousemove", onMouseMove);
        
        // Shimmer animation
        const shimmerInterval = setInterval(() => {
            setShimmer(prev => (prev + 1) % 100);
        }, 30);
        
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            clearInterval(shimmerInterval);
        };
    }, [allowDecorativeEffects, onMouseMove]);

    return (
        <motion.div
            className="relative mx-auto h-[200px] w-[320px] sm:h-[240px] sm:w-[380px]"
            animate={{ x: offset.x, y: offset.y }}
            transition={{ duration: DURATION.hover, ease: EASING.standard }}
            aria-hidden="true"
        >
            {/* Card Background with Gradient */}
            <div className="absolute inset-0 rounded-[28px] border border-purple-400/30 bg-gradient-to-br from-purple-500/20 via-indigo-500/15 to-cyan-500/10 shadow-[0_20px_80px_rgba(99,102,241,0.4),0_0_40px_rgba(168,85,247,0.3)] backdrop-blur-2xl overflow-hidden">
                {/* Animated shimmer overlay */}
                <div 
                    className="absolute inset-0 opacity-30"
                    style={{
                        background: `linear-gradient(90deg, transparent ${shimmer}%, rgba(255,255,255,0.1) ${shimmer + 5}%, transparent ${shimmer + 10}%)`
                    }}
                />
            </div>

            {/* AI Badge Top Left */}
            <div className="absolute left-6 top-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/30 border border-purple-400/40 backdrop-blur-sm">
                <Brain size={14} className="text-purple-200 animate-pulse" />
                <span className="text-[10px] font-bold text-white uppercase tracking-wider">AI Score</span>
            </div>

            {/* Claude Opus Badge Top Right */}
            <div className="absolute right-6 top-6 px-3 py-1 rounded-full bg-indigo-500/25 border border-indigo-300/35 backdrop-blur-sm">
                <span className="text-[9px] font-semibold text-indigo-100 tracking-wide">Claude Opus</span>
            </div>

            {/* Main Score Display - Center */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, ease: EASING.smooth }}
                >
                    <div className="text-6xl sm:text-7xl font-heading font-bold bg-gradient-to-br from-white via-purple-100 to-cyan-100 bg-clip-text text-transparent mb-1">
                        840
                    </div>
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                        <Sparkles size={12} className="text-green-400" />
                        <span className="text-xs font-semibold text-green-400 uppercase tracking-wider">Pioneer Tier</span>
                    </div>
                    <div className="text-[10px] text-purple-200/80 uppercase tracking-widest">Real-time Analysis</div>
                </motion.div>
            </div>

            {/* Signal Indicators Bottom */}
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
                {/* Left: Confidence */}
                <div className="flex items-center gap-1.5">
                    <div className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
                    <span className="text-[9px] text-cyan-200/90 font-medium">92% Confident</span>
                </div>

                {/* Right: Signal Count */}
                <div className="flex items-center gap-1">
                    <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-1.5 w-1.5 rounded-full bg-purple-400/80" />
                        ))}
                    </div>
                    <span className="text-[9px] text-purple-200/90 font-medium ml-1">5 Signals</span>
                </div>
            </div>

            {/* Glow effect */}
            <div className="absolute inset-0 rounded-[28px] bg-gradient-to-br from-purple-500/20 to-cyan-500/10 blur-2xl -z-10 animate-pulse" />
        </motion.div>
    );
}

function DemoProfilePanel({ profile, loading, activeKey }) {
    const p = profile || getDemoFallback(activeKey);
    const tier = tierMeta(p?.tier);
    const score = p?.score || 0;
    const confidence = p?.confidence || 0;
    const initials = (p?.name || activeKey)
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    return (
        <SpotlightCard className="p-6 md:p-8" spotlightColor={`${tier.color}33`}>
            <div className="flex items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                    <div
                        className="h-11 w-11 rounded-full flex items-center justify-center font-heading font-bold"
                        style={{ color: tier.color, backgroundColor: `${tier.color}20` }}
                    >
                        {initials}
                    </div>
                    <div>
                        <p className="text-text-primary text-sm font-semibold">{p?.name}</p>
                        <p className="text-text-secondary text-xs">{p?.institution || p?.evidence_summary?.academic?.institution || "Student profile"}</p>
                    </div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full ${tier.bg} ${tier.text} ring-1 ${tier.ring}`}>{p?.tier}</span>
            </div>

            <div className="flex items-end gap-2 mb-2">
                <CountUp
                    target={score}
                    duration={1.3}
                    className="text-5xl font-bold tracking-tight"
                    style={{ color: tier.color }}
                />
                <span className="text-text-secondary text-sm pb-2">/ 900</span>
            </div>

            <p className="text-text-secondary text-xs mb-4">Confidence {(confidence * 100).toFixed(0)}%</p>
            {loading && <p className="text-text-secondary/70 text-xs mb-3">Refreshing live demo profile...</p>}

            <div className="space-y-2 mb-4">
                {signals.map((sig, index) => {
                    const keyMap = {
                        "Academic Intelligence": "academic",
                        "Fee Payment History": "fee_payment",
                        "Developer / Professional": "developer_professional",
                        "Social / Creator": "social_creator",
                        "Coverage & Consistency": "coverage_consistency",
                    };
                    const key = keyMap[sig.name];
                    const value = p?.signal_scores?.[key]?.score ?? Math.max(30, 72 - index * 8);
                    return (
                        <div key={sig.name}>
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-text-secondary">{sig.name}</span>
                                <span className="font-mono text-text-primary">{value}</span>
                            </div>
                            <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                <motion.div
                                    className="h-full rounded-full"
                                    style={{ backgroundColor: sig.color }}
                                    initial={{ width: 0 }}
                                    whileInView={{ width: `${value}%` }}
                                    viewport={{ once: true }}
                                    transition={{ duration: DURATION.sectionReveal, ease: EASING.smooth }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <Link
                to={`/genz-credit-scoring/result/demo-${activeKey}`}
                className="inline-flex items-center gap-1 text-indigo-300 hover:gap-2 transition-all text-sm font-medium"
                data-testid={`demo-view-${activeKey}`}
            >
                Open Full Dashboard <ChevronRight size={14} />
            </Link>
        </SpotlightCard>
    );
}

function PersonaWidget() {
    const [college, setCollege] = useState("");
    const [course, setCourse] = useState("engineering");
    const [year, setYear] = useState(3);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const estimate = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/api/persona/estimate?college=${encodeURIComponent(college)}&course=${course}&year=${year}`);
            setResult(res.data);
        } catch {
            setResult(null);
        }
        setLoading(false);
    };

    return (
        <SpotlightCard className="p-6 md:p-8 border-purple-soft/30" spotlightColor="rgba(168,85,247,0.18)" data-testid="persona-widget">
            <div className="flex items-center gap-2 mb-2">
                <Search size={18} className="text-purple-soft" />
                <h3 className="font-heading text-lg text-text-primary">Soft Persona Estimate</h3>
            </div>
            <p className="text-text-secondary text-sm mb-5">Get a fast confidence-aligned estimate before completing full verification.</p>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <input
                    value={college}
                    onChange={(event) => setCollege(event.target.value)}
                    type="text"
                    placeholder="College name"
                    className="glass-input sm:col-span-2 text-sm"
                    data-testid="persona-college-input"
                />
                <select value={course} onChange={(event) => setCourse(event.target.value)} className="glass-input text-sm" data-testid="persona-course-select">
                    <option value="engineering">Engineering</option>
                    <option value="business">Business</option>
                    <option value="arts_science">Arts & Science</option>
                </select>
                <select value={year} onChange={(event) => setYear(Number(event.target.value))} className="glass-input text-sm" data-testid="persona-year-select">
                    {[1, 2, 3, 4].map((y) => <option key={y} value={y}>Year {y}</option>)}
                </select>
            </div>

            <Magnet strength={0.16}>
                <button
                    onClick={estimate}
                    disabled={loading}
                    className="mt-4 bg-purple-soft/18 text-purple-100 border border-purple-soft/35 px-6 py-2.5 rounded-xl hover:bg-purple-soft/24 transition-all text-sm disabled:opacity-50"
                    data-testid="persona-estimate-btn"
                >
                    {loading ? "Estimating..." : "Estimate My Range"}
                </button>
            </Magnet>

            {result && (
                <motion.div
                    initial={{ opacity: 0, filter: "blur(6px)", y: 12 }}
                    animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
                    transition={{ duration: DURATION.sectionReveal, ease: EASING.smooth }}
                    className="mt-5 p-4 rounded-xl border border-white/10 bg-white/[0.03]"
                    data-testid="persona-result"
                >
                    <p className="text-text-secondary text-xs uppercase tracking-widest mb-1">Estimated Range</p>
                    <div className="flex items-center gap-2 mb-2">
                        <CountUp target={result.estimated_score_range?.[0] || 0} className="text-3xl text-cyan-signal font-bold" duration={1.2} />
                        <span className="text-text-secondary">—</span>
                        <CountUp target={result.estimated_score_range?.[1] || 0} className="text-3xl text-cyan-signal font-bold" duration={1.2} />
                    </div>
                    <p className="text-text-secondary text-sm">
                        {result.institution} · {result.course_category} · confidence-driven soft estimate
                    </p>
                    <Link to="/genz-credit-scoring/persona" className="mt-2 inline-flex items-center gap-1 text-purple-300 text-sm hover:gap-2 transition-all">
                        Detailed Persona View <ChevronRight size={14} />
                    </Link>
                </motion.div>
            )}
        </SpotlightCard>
    );
}

export default function ProductLanding() {
    const { prefersReducedMotion, isTouch } = useMotionPreferences();
    const [demos, setDemos] = useState({ priya: null, rahul: null, arjun: null });
    const [activeDemo, setActiveDemo] = useState("priya");
    const [demoLoading, setDemoLoading] = useState(false);

    useEffect(() => {
        const fetchDemos = async () => {
            setDemoLoading(true);
            try {
                const [p, r, a] = await Promise.all([
                    api.get("/api/score/demo/priya"),
                    api.get("/api/score/demo/rahul"),
                    api.get("/api/score/demo/arjun"),
                ]);
                setDemos({ priya: p.data, rahul: r.data, arjun: a.data });
            } catch {
                setDemos({
                    priya: getDemoFallback("priya"),
                    rahul: getDemoFallback("rahul"),
                    arjun: getDemoFallback("arjun"),
                });
            }
            setDemoLoading(false);
        };
        fetchDemos();
    }, []);

    const dockItems = useMemo(
        () =>
            demoProfiles.map((profile) => ({
                key: profile.key,
                label: profile.label,
                icon: profile.icon,
            })),
        [],
    );

    return (
        <div className="pt-16 overflow-hidden" data-testid="product-landing">
            <section className="relative px-6 md:px-12 lg:px-24 pt-20 md:pt-28 pb-24 min-h-[92vh] flex items-center">
                <Aurora className="z-0" colors={["#6366f1", "#06b6d4", "#a855f7", "#ec4899"]} speed={9} />
                {!prefersReducedMotion && !isTouch && (
                    <video
                        className="absolute inset-0 h-full w-full object-cover opacity-15 pointer-events-none"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        poster="/media/hero-ambient-poster.jpg"
                    >
                        <source src="/media/hero-ambient.webm" type="video/webm" />
                    </video>
                )}

                <div className="relative z-10 w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <motion.p
                            className="text-xs uppercase tracking-[0.34em] text-cyan-signal font-semibold mb-4"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: DURATION.heroEntrance, ease: EASING.smooth }}
                        >
                            Alternative Credit Scoring
                        </motion.p>

                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-[4.2rem] font-bold leading-[1.15] tracking-tight text-text-primary mb-3 break-words">
                            <SplitText text="Your Credit Score, Reimagined for Gen Z" duration={0.03} />
                        </h1>

                        <p className="text-text-secondary text-base md:text-lg leading-relaxed max-w-xl mb-8 break-words">
                            {prefersReducedMotion ? (
                                "We analyze your digital footprint — GitHub, academics, social presence, fee payment & installment patterns, online spend persona — to build a credit profile that actually represents you. No credit history required."
                            ) : (
                                <TypewriterText text="We analyze your digital footprint — GitHub, academics, social presence, fee payment & installment patterns, online spend persona — to build a credit profile that actually represents you. No credit history required." speed={24} />
                            )}
                        </p>

                        <div className="flex flex-wrap gap-3 mb-7">
                            <Magnet strength={0.18}>
                                <ClickSpark sparkCount={8}>
                                    <Link
                                        to="/genz-credit-scoring/flow"
                                        className="btn-glow inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-soft text-white font-semibold"
                                        data-testid="hero-get-score-btn"
                                    >
                                        Get My Credit Score <ArrowRight size={18} />
                                    </Link>
                                </ClickSpark>
                            </Magnet>

                            <Magnet strength={0.14}>
                                <a
                                    href="#demo-section"
                                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-white/15 bg-white/[0.03] text-text-primary font-medium hover:bg-white/[0.07]"
                                    data-testid="hero-see-demo-btn"
                                >
                                    <Sparkles size={16} className="text-cyan-signal" /> Explore Live Demo
                                </a>
                            </Magnet>
                        </div>

                        <BlurText className="text-text-secondary text-sm max-w-xl">
                            Consent-first scoring with explainable AI, verified evidence lineage, and institutional data depth from India's campus ecosystem.
                        </BlurText>
                    </div>

                    <ScrollReveal delay={0.15}>
                        <FloatingCreditOrb />
                    </ScrollReveal>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 py-16">
                <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
                    {trustStats.map((stat) => (
                        <SpotlightCard key={stat.label} className="p-5 md:p-6 text-center" spotlightColor="rgba(6,182,212,0.15)">
                            <p className="font-mono text-lg sm:text-2xl md:text-4xl font-bold text-cyan-signal tracking-tight tabular-nums break-words" data-testid={stat.testId}>
                                <CountUp target={stat.value} suffix={stat.suffix} duration={2.4} />
                            </p>
                            <p className="text-text-secondary text-xs md:text-sm mt-2">{stat.label}</p>
                        </SpotlightCard>
                    ))}
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 py-24">
                <div className="max-w-6xl mx-auto">
                    <ScrollReveal>
                        <div className="flex items-center gap-3 mb-3">
                            <Brain size={32} className="text-purple-soft animate-pulse" />
                            <h2 className="font-heading text-3xl md:text-4xl text-text-primary font-semibold">AI-Powered Intelligence Flow</h2>
                        </div>
                    </ScrollReveal>
                    <ScrollReveal delay={0.06}>
                        <p className="text-text-secondary mb-12 max-w-2xl flex items-center gap-2">
                            <Sparkles size={16} className="text-purple-soft shrink-0" />
                            <span>GenZGPT Agent analyzes your data through 4 intelligent stages, delivering instant credit insights.</span>
                        </p>
                    </ScrollReveal>

                    <div className="grid md:grid-cols-4 gap-5 relative">
                        <div className="hidden md:block absolute top-8 left-[9%] right-[9%] h-px bg-gradient-to-r from-indigo-500/20 via-cyan-400/35 to-purple-soft/25" />
                        {howItWorks.map((step, index) => (
                            <ScrollReveal key={step.title} delay={index * 0.08}>
                                <SpotlightCard className="p-5 h-full" spotlightColor="rgba(99,102,241,0.16)">
                                    <motion.div
                                        className={`w-10 h-10 rounded-xl ${step.bg} flex items-center justify-center mb-4`}
                                        animate={{ y: [0, -2, 0] }}
                                        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: index * 0.1 }}
                                    >
                                        <step.icon size={18} className={step.color} />
                                    </motion.div>
                                    <p className="font-mono text-xs text-text-secondary/60">{step.step}</p>
                                    <p className="text-text-primary font-heading text-lg">{step.title}</p>
                                    <p className="text-text-secondary text-sm mt-1">{step.desc}</p>
                                </SpotlightCard>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 py-24">
                <div className="max-w-6xl mx-auto">
                    <ScrollReveal>
                        <h2 className="font-heading text-3xl md:text-4xl text-text-primary font-semibold mb-3">
                            <GradientText className="flex items-center gap-3">
                                <Sparkles size={28} className="text-purple-soft animate-pulse" />
                                6 Signal Intelligence
                            </GradientText>
                        </h2>
                    </ScrollReveal>
                    <ScrollReveal delay={0.06}>
                        <p className="text-text-secondary mb-12 max-w-2xl">GenZGPT Agent analyzes 6 signals in real-time. Every score is AI-weighted, explainable, and backed by verified evidence.</p>
                    </ScrollReveal>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {signals.map((signal, index) => (
                            <TiltCard key={signal.name} className="tilt-card" glow>
                                <SpotlightCard className="p-5 h-full" spotlightColor={`${signal.color}2e`}>
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <signal.icon size={18} style={{ color: signal.color }} />
                                        <motion.span
                                            className="font-mono text-xs font-bold"
                                            style={{ color: signal.color }}
                                            initial={{ opacity: 0, x: 10 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: 0.22 + index * 0.06 }}
                                        >
                                            {signal.weight}%
                                        </motion.span>
                                    </div>
                                    <p className="text-text-primary font-heading text-base mb-1">{signal.name}</p>
                                    <p className="text-text-secondary text-sm">{signal.detail}</p>
                                    <div className="mt-4 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                        <motion.div
                                            className="h-full rounded-full"
                                            style={{ backgroundColor: signal.color }}
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${signal.weight}%` }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 1.1, delay: 0.15 + index * 0.06, ease: EASING.smooth }}
                                        />
                                    </div>
                                </SpotlightCard>
                            </TiltCard>
                        ))}
                    </div>
                </div>
            </section>

            <section id="demo-section" className="px-6 md:px-12 lg:px-24 py-24 scroll-mt-24">
                <div className="max-w-6xl mx-auto">
                    <ScrollReveal>
                        <p className="text-xs uppercase tracking-[0.3em] text-cyan-signal font-semibold mb-3">Live Profiles</p>
                        <h2 className="font-heading text-3xl md:text-4xl text-text-primary font-semibold mb-3">Proof Through Real Scoring Journeys</h2>
                        <p className="text-text-secondary mb-8">Switch personas below to inspect how evidence quality changes outcomes.</p>
                    </ScrollReveal>

                    <Dock items={dockItems} activeKey={activeDemo} onSelect={setActiveDemo} className="mb-6" />
                    <DemoProfilePanel profile={demos[activeDemo]} loading={demoLoading} activeKey={activeDemo} />
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="score-simulator-section">
                <div className="max-w-5xl mx-auto">
                    <ScrollReveal>
                        <h2 className="font-heading text-3xl md:text-4xl text-text-primary font-semibold mb-3">Premium Score Simulator</h2>
                        <p className="text-text-secondary mb-8">A realistic preview of score range movement as your signal mix improves.</p>
                    </ScrollReveal>
                    <ScrollReveal delay={0.06}>
                        <ScoreSimulator />
                    </ScrollReveal>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 py-24">
                <div className="max-w-4xl mx-auto">
                    <PersonaWidget />
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="iwebtechno-section">
                <div className="max-w-6xl mx-auto">
                    <SpotlightCard className="p-8 md:p-10 bg-[#060b17]/90 border-white/10" spotlightColor="rgba(6,182,212,0.1)">
                        <p className="text-xs uppercase tracking-[0.3em] text-cyan-signal font-semibold mb-3">Institutional Data Moat</p>
                        <h2 className="font-heading text-3xl md:text-4xl text-text-primary font-semibold mb-3">iWebTechno-Powered Campus Trust Layer</h2>
                        <p className="text-text-secondary max-w-3xl mb-8">
                            Built on multi-year campus network records with verified ERP and fee ledger provenance. This is infrastructure-level trust, not startup guesswork.
                        </p>

                        <div className="grid md:grid-cols-3 gap-4">
                            {[
                                { icon: Landmark, label: "Campuses", value: 900, suffix: "+" },
                                { icon: Users, label: "Students in active graph", value: 1500000, suffix: "+" },
                                { icon: Database, label: "Years of historical records", value: 20, suffix: "" },
                            ].map((item, index) => (
                                <motion.div
                                    key={item.label}
                                    className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.75, delay: index * 0.12, ease: EASING.smooth }}
                                >
                                    <item.icon size={16} className="text-cyan-signal mb-2" />
                                    <p className="font-mono text-3xl text-cyan-signal font-bold">
                                        <CountUp target={item.value} suffix={item.suffix} duration={3.1} />
                                    </p>
                                    <p className="text-text-secondary text-sm mt-1">{item.label}</p>
                                </motion.div>
                            ))}
                        </div>
                    </SpotlightCard>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="competitor-section">
                <div className="max-w-6xl mx-auto">
                    <ScrollReveal>
                        <h2 className="font-heading text-3xl md:text-4xl text-text-primary font-semibold mb-3">Credibility Comparison</h2>
                        <p className="text-text-secondary mb-8">Readable, lender-trust-first comparison against legacy and global alternatives.</p>
                    </ScrollReveal>

                    <SpotlightCard className="p-4 md:p-6">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm leading-relaxed" data-testid="competitor-table">
                                <thead>
                                    <tr className="border-b border-white/10 text-xs uppercase tracking-widest">
                                        <th className="text-left py-3 px-3 text-text-secondary font-semibold">Feature</th>
                                        {["GenZDealZ", "TomoCredit", "Self.inc", "Experian Boost", "CIBIL"].map((competitor) => (
                                            <th key={competitor} className={`text-center py-3 px-2 font-semibold ${competitor === "GenZDealZ" ? "text-cyan-signal" : "text-text-secondary/70"}`}>
                                                {competitor}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        ["Academic Data", true, false, false, false, false],
                                        ["Fee Intelligence", true, false, false, false, false],
                                        ["Persona Matching", true, false, false, false, false],
                                        ["India-native underwriting", true, false, false, false, true],
                                        ["Explainable evidence lineage", true, false, false, true, false],
                                        ["Consent-first architecture", true, false, false, true, false],
                                    ].map(([feature, ...values], rowIndex) => (
                                        <motion.tr
                                            key={feature}
                                            className="border-b border-white/5"
                                            initial={{ opacity: 0, y: 10 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.45, delay: rowIndex * 0.06, ease: EASING.standard }}
                                        >
                                            <td className="py-3 px-3 text-text-primary font-medium whitespace-nowrap">{feature}</td>
                                            {values.map((value, idx) => (
                                                <td key={`${feature}-${idx}`} className="text-center py-3 px-2">
                                                    {value ? (
                                                        <CheckCircle2 size={15} className={idx === 0 ? "text-green-pioneer mx-auto" : "text-green-pioneer/65 mx-auto"} />
                                                    ) : (
                                                        <span className="text-red-starter/40 text-lg">-</span>
                                                    )}
                                                </td>
                                            ))}
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </SpotlightCard>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 py-24 pb-16">
                <div className="max-w-4xl mx-auto text-center">
                    <ScrollReveal>
                        <h2 className="font-heading text-3xl md:text-4xl text-text-primary font-semibold mb-3">Ready to unlock your lender-ready potential?</h2>
                        <p className="text-text-secondary mb-8">Start with your strongest verified signals. Build confidence one source at a time.</p>
                    </ScrollReveal>
                    <Magnet strength={0.16}>
                        <Link
                            to="/genz-credit-scoring/flow"
                            className="inline-flex items-center gap-2 px-10 py-4 rounded-xl bg-blue-primary text-white font-semibold btn-glow"
                            data-testid="footer-cta-btn"
                        >
                            Get My Credit Score <ArrowRight size={18} />
                        </Link>
                    </Magnet>
                </div>
            </section>

            <footer className="relative border-t border-white/10 bg-[#060d1c]/85 backdrop-blur-xl" data-testid="landing-footer">
                <div className="shimmer-separator absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-300/45 to-transparent" />
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-9">
                        <div>
                            <p className="font-heading text-lg font-semibold text-text-primary mb-2">GenZ Credit Score</p>
                            <p className="text-text-secondary text-sm leading-relaxed mb-3">Institution-grade alternative scoring for India&apos;s next generation.</p>
                            <p className="text-text-primary/80 text-sm font-medium">Made for India&apos;s Gen Z</p>
                        </div>
                        <div>
                            <p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-3">Product</p>
                            <div className="space-y-2 text-sm">
                                <Link to="/genz-credit-scoring/flow" className="block text-text-secondary hover:text-text-primary">Score Flow</Link>
                                <Link to="/genz-credit-scoring/persona" className="block text-text-secondary hover:text-text-primary">Persona Estimate</Link>
                                <Link to="/genz-credit-scoring/improve" className="block text-text-secondary hover:text-text-primary">Improve Score</Link>
                            </div>
                        </div>
                        <div>
                            <p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-3">Trust</p>
                            <div className="space-y-2 text-sm">
                                <Link to="/privacy-policy#consent" className="block text-text-secondary hover:text-text-primary">Consent Framework</Link>
                                <Link to="/privacy-policy#data-collected" className="block text-text-secondary hover:text-text-primary">Evidence Lineage</Link>
                                <Link to="/privacy-policy#security" className="block text-text-secondary hover:text-text-primary">Data Security</Link>
                            </div>
                        </div>
                        <div>
                            <p className="text-text-secondary text-xs uppercase tracking-widest font-semibold mb-3">Legal</p>
                            <div className="space-y-2 text-sm">
                                <Link to="/privacy-policy" className="block text-text-secondary hover:text-text-primary">Privacy Policy</Link>
                                <Link to="/terms-of-service" className="block text-text-secondary hover:text-text-primary">Terms of Service</Link>
                                <Link to="/privacy-policy#your-rights" className="block text-text-secondary hover:text-text-primary">DPDPA Compliance</Link>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-white/5 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <p className="text-text-secondary/60 text-xs">&copy; 2026 GenZDealZ.ai. All rights reserved.</p>
                        <div className="flex items-center gap-3">
                            <a href="https://genzdealz.ai" target="_blank" rel="noopener noreferrer" className="h-8 w-8 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center text-text-secondary hover:text-white hover:shadow-[0_0_18px_rgba(99,102,241,0.25)] transition-all">
                                <Globe size={14} />
                            </a>
                            <a href="https://instagram.com/genzdealz" target="_blank" rel="noopener noreferrer" className="h-8 w-8 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center text-text-secondary hover:text-white hover:shadow-[0_0_18px_rgba(99,102,241,0.25)] transition-all">
                                <Share2 size={14} />
                            </a>
                            <a href="https://github.com/smit-lakhani-13/genzdealz-credit-score" target="_blank" rel="noopener noreferrer" className="h-8 w-8 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center text-text-secondary hover:text-white hover:shadow-[0_0_18px_rgba(99,102,241,0.25)] transition-all">
                                <Code2 size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
